/**
 * mediaUtils.js
 * Zentrale Hilfsfunktionen für authentifizierte Media-URLs.
 *
 * Verwendung:
 *   import { getSecureMediaUrl, normalizeAndSecureMediaUrl } from '../utils/mediaUtils';
 *
 * Hintergrund:
 *   Alle /media/clients/-Requests gehen durch media-proxy.php auf channelserver.
 *   Die Auth-Reihenfolge im Proxy ist:
 *     1. Authorization: Bearer <token>   → fetch() / makeAuthenticatedRequest()
 *     2. ?token=<token>                  → diese Utility-Funktion
 *     3. Cookie hejvi_access_token       → browser-native (img, video, background)
 *
 *   Cookie-Auth ist zuverlässig für same-origin Requests, versagt aber bei:
 *     - abgelaufenem Cookie (TTL-Mismatch nach checkExistingAuth)
 *     - <video crossOrigin="anonymous"> / CORS-Kontext
 *     - Safari ITP in bestimmten Privacy-Modes
 *   Daher: token-in-URL als Defense-in-Depth für alle browser-nativen Media-Requests.
 */

/**
 * Hängt den JWT als ?token= an alle /media/clients/-URLs.
 * Nicht-Media-URLs (ext. CDN, data:, blob:) werden unverändert zurückgegeben.
 *
 * @param {string} url - Beliebige URL (relativ oder absolut)
 * @returns {string} URL mit ?token= für /media/clients/-Pfade, sonst unverändert
 */
export const getSecureMediaUrl = (url) => {
  if (!url) return '';
  const str = String(url);
  if (!str.includes('/media/clients/')) return str;
  const token = localStorage.getItem('hejvi_access_token') || '';
  if (!token) return str; // kein Token → URL unverändert (Auth schlägt ohnehin fehl)
  const sep = str.includes('?') ? '&' : '?';
  return `${str}${sep}token=${encodeURIComponent(token)}`;
};

/**
 * Normalisiert eine relative oder absolute URL zu einer absoluten URL
 * und hängt dann den Token an (für /media/clients/-Pfade).
 *
 * @param {string} url     - Rohe URL aus der DB (relativ oder absolut)
 * @param {string} baseUrl - appConfig.baseUrl, z.B. "https://app.hejvi.de"
 * @returns {string} Vollständige, authentifizierte URL
 */
export const normalizeAndSecureMediaUrl = (url, baseUrl) => {
  if (!url) return '';
  let cleanPath = String(url);

  if (!cleanPath.startsWith('http')) {
    cleanPath = cleanPath.startsWith('/') ? cleanPath.slice(1) : cleanPath;
    const base = (baseUrl || '').endsWith('/') ? (baseUrl || '') : `${baseUrl || ''}/`;
    cleanPath = `${base}${cleanPath}`;
  }

  return getSecureMediaUrl(cleanPath);
};
